import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Move, Trash2, Download, RotateCcw, ZoomIn, ZoomOut, Eye, EyeOff, Lock, Unlock, Type, Check, X, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';

/**
 * LayerEditor - A canvas-based editor for manipulating extracted slide layers
 *
 * Features:
 * - Drag layers to reposition
 * - Resize layers with handles
 * - Delete layers
 * - Toggle layer visibility
 * - Edit text content (double-click on text layers)
 * - Export modified image
 */
export default function LayerEditor({
  extractedLayers,
  onClose,
  onExport,
  onSave
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const textInputRef = useRef(null);

  // Layer state - each layer has position, size, visibility, locked status
  const [layers, setLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Text editing state
  const [editingTextLayerId, setEditingTextLayerId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [textStyles, setTextStyles] = useState({
    fontSize: 48,
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    color: '#ffffff'
  });

  // Canvas state
  const [canvasSize, setCanvasSize] = useState({ width: 1920, height: 1080 });
  const [scale, setScale] = useState(0.5);

  // Inpainting status
  const [inpaintingSuccess, setInpaintingSuccess] = useState(false);

  // Track if layers have been initialized to prevent re-initialization on save
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize layers from extracted data
  useEffect(() => {
    // Skip re-initialization if already initialized (prevents crash when parent updates extractedLayers)
    if (isInitialized) return;

    if (extractedLayers?.layers) {
      // Check if layers are already in internal format (have 'x' property instead of 'bbox')
      const firstLayer = extractedLayers.layers[0];
      if (firstLayer && 'x' in firstLayer && !firstLayer.bbox) {
        // Layers are already in internal format, use them directly
        setLayers(extractedLayers.layers);
        setIsInitialized(true);
        return;
      }

      // Check if this is template-generated (no image data, just text)
      const isTemplateSource = extractedLayers.source === 'template';

      const initialLayers = extractedLayers.layers.map((layer, idx) => {
        const hasImage = layer.imageSrc || layer.image;
        // Template-generated text layers should start in edit mode
        const isTemplateTextLayer = isTemplateSource && layer.type === 'text' && !hasImage;

        return {
          id: layer.id,
          type: layer.type,
          content: layer.content,
          image: null, // Will be loaded from imageSrc
          // Handle both fresh extraction (layer.image) and loaded from save (layer.imageSrc)
          imageSrc: layer.imageSrc || layer.image,
          x: layer.x ?? layer.bbox?.[0] ?? 0,
          y: layer.y ?? layer.bbox?.[1] ?? 0,
          width: layer.width ?? (layer.bbox ? layer.bbox[2] - layer.bbox[0] : 100),
          height: layer.height ?? (layer.bbox ? layer.bbox[3] - layer.bbox[1] : 50),
          visible: layer.visible !== undefined ? layer.visible : true,
          locked: layer.locked || false,
          zIndex: layer.zIndex ?? idx + 1,
          // Text-specific properties - template layers are pre-edited
          isTextEdited: layer.isTextEdited || isTemplateTextLayer,
          editedText: layer.editedText || layer.content || '',
          // Use provided textStyle for template layers, or generate defaults
          textStyle: layer.textStyle || {
            // Calculate font size based on layer height (more accurate)
            fontSize: Math.max(16, Math.min(120, Math.round((layer.height || (layer.bbox ? layer.bbox[3] - layer.bbox[1] : 50)) * 0.7))),
            fontFamily: 'Arial, sans-serif',
            fontWeight: 'bold',
            // Default to white (common for slides) - will be updated after color detection
            color: '#ffffff',
            textAlign: 'left'
          },
          // Template layers don't show original image
          showOriginalWhenNotEditing: isTemplateTextLayer ? false : (layer.showOriginalWhenNotEditing !== undefined ? layer.showOriginalWhenNotEditing : true),
          // Track extraction method for UI indicators
          extractionMethod: layer.extraction_method || layer.extractionMethod || null
        };
      });
      setLayers(initialLayers);
      setIsInitialized(true);

      // Helper function to convert RGB to hex
      const rgbToHex = (r, g, b) => {
        return '#' + [r, g, b].map(x => {
          const hex = Math.round(x).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('');
      };

      // Load layer images and detect dominant color for text layers
      // Skip layers without imageSrc (e.g., template-generated text layers)
      initialLayers.forEach((layer, idx) => {
        if (!layer.imageSrc) {
          // For template-generated text layers, mark as ready for editing
          if (layer.type === 'text') {
            setLayers(prev => prev.map(l =>
              l.id === layer.id ? { ...l, isTextEdited: true, showOriginalWhenNotEditing: false } : l
            ));
          }
          return;
        }

        const img = new Image();
        img.onload = () => {
          console.log(`Layer ${layer.id} image loaded: ${img.width}x${img.height}`);

          // For text layers, try to detect the dominant color
          // Skip detection if backend (Gemini) or template already provided a textStyle color
          let detectedColor = null;
          const originalLayerData = extractedLayers.layers[idx];
          const hasBackendTextColor = originalLayerData?.textStyle?.color;

          if (layer.type === 'text' && !hasBackendTextColor) {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);

              // Sample colors from the image
              const imageData = ctx.getImageData(0, 0, img.width, img.height).data;
              const colorCounts = {};
              let totalPixels = 0;

              for (let i = 0; i < imageData.length; i += 4) {
                const r = imageData[i];
                const g = imageData[i + 1];
                const b = imageData[i + 2];
                const a = imageData[i + 3];

                // Skip transparent/semi-transparent pixels (background/shadows)
                if (a < 200) continue;

                totalPixels++;

                // Quantize to reduce color variations
                const key = `${Math.round(r/16)*16},${Math.round(g/16)*16},${Math.round(b/16)*16}`;
                colorCounts[key] = (colorCounts[key] || 0) + 1;
              }

              // Sort colors by frequency
              const sortedColors = Object.entries(colorCounts).sort((a, b) => b[1] - a[1]);

              console.log(`Color detection for ${layer.id}:`, sortedColors.slice(0, 5), `total pixels: ${totalPixels}`);

              // The most common color is usually the background, not the text.
              // Use the second most common color (text) if there are multiple distinct colors.
              // If only one color, use it directly.
              if (sortedColors.length >= 2) {
                const [r, g, b] = sortedColors[1][0].split(',').map(Number);
                detectedColor = rgbToHex(r, g, b);
                console.log(`Detected text color for ${layer.id}: ${detectedColor} (minority color)`);
              } else if (sortedColors.length === 1) {
                const [r, g, b] = sortedColors[0][0].split(',').map(Number);
                detectedColor = rgbToHex(r, g, b);
                console.log(`Detected color for ${layer.id}: ${detectedColor} (single color)`);
              }
            } catch (e) {
              console.warn('Could not detect text color:', e);
            }
          }

          setLayers(prev => prev.map(l =>
            l.id === layer.id ? {
              ...l,
              image: img,
              textStyle: detectedColor ? { ...l.textStyle, color: detectedColor } : l.textStyle
            } : l
          ));
        };
        img.onerror = (e) => {
          console.error(`Layer ${layer.id} image failed to load:`, e);
        };

        img.src = layer.imageSrc;
      });

      // Set canvas size from extracted data
      if (extractedLayers.canvas) {
        setCanvasSize({
          width: extractedLayers.canvas.width,
          height: extractedLayers.canvas.height
        });
      }

      // Track inpainting status
      setInpaintingSuccess(extractedLayers.inpainting_success || false);
    }

    // Load background
    if (extractedLayers?.background) {
      const bgImg = new Image();
      bgImg.onload = () => setBackgroundImage(bgImg);
      bgImg.src = extractedLayers.background;
    }
  }, [extractedLayers]);

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw checkered background for transparency
    const checkerSize = 10;
    for (let x = 0; x < canvas.width; x += checkerSize) {
      for (let y = 0; y < canvas.height; y += checkerSize) {
        ctx.fillStyle = ((x + y) / checkerSize) % 2 === 0 ? '#f0f0f0' : '#ffffff';
        ctx.fillRect(x, y, checkerSize, checkerSize);
      }
    }

    // Draw background image
    if (backgroundImage) {
      ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
    }

    // Sort layers by zIndex and draw visible ones
    const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex);

    sortedLayers.forEach(layer => {
      if (!layer.visible) return;

      // If this is an edited text layer, draw text instead of image
      if (layer.type === 'text' && layer.isTextEdited && layer.editedText) {
        const style = layer.textStyle || {};
        ctx.font = `${style.fontWeight || 'bold'} ${style.fontSize || 48}px ${style.fontFamily || 'Arial, sans-serif'}`;
        ctx.fillStyle = style.color || '#ffffff';
        ctx.textBaseline = 'top';
        ctx.textAlign = style.textAlign || 'left';

        // Word wrap text within the layer bounds
        const words = layer.editedText.split(' ');
        let line = '';
        let y = layer.y + 10;
        const lineHeight = (style.fontSize || 48) * 1.2;
        const maxWidth = layer.width - 20;
        const startX = style.textAlign === 'center' ? layer.x + layer.width / 2 :
                       style.textAlign === 'right' ? layer.x + layer.width - 10 : layer.x + 10;

        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), startX, y);
            line = words[i] + ' ';
            y += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), startX, y);
      } else if (layer.image) {
        // Draw the layer image at its position
        console.log(`Drawing layer ${layer.id} at (${layer.x}, ${layer.y}) size ${layer.width}x${layer.height}`);
        ctx.drawImage(
          layer.image,
          layer.x,
          layer.y,
          layer.width,
          layer.height
        );
      } else {
        console.log(`Layer ${layer.id} has no image loaded yet`);
      }

      // Draw selection box if selected
      if (layer.id === selectedLayerId) {
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(layer.x - 2, layer.y - 2, layer.width + 4, layer.height + 4);
        ctx.setLineDash([]);

        // Draw resize handles
        if (!layer.locked) {
          const handleSize = 8;
          const handles = [
            { x: layer.x - handleSize/2, y: layer.y - handleSize/2, cursor: 'nw-resize', type: 'nw' },
            { x: layer.x + layer.width/2 - handleSize/2, y: layer.y - handleSize/2, cursor: 'n-resize', type: 'n' },
            { x: layer.x + layer.width - handleSize/2, y: layer.y - handleSize/2, cursor: 'ne-resize', type: 'ne' },
            { x: layer.x + layer.width - handleSize/2, y: layer.y + layer.height/2 - handleSize/2, cursor: 'e-resize', type: 'e' },
            { x: layer.x + layer.width - handleSize/2, y: layer.y + layer.height - handleSize/2, cursor: 'se-resize', type: 'se' },
            { x: layer.x + layer.width/2 - handleSize/2, y: layer.y + layer.height - handleSize/2, cursor: 's-resize', type: 's' },
            { x: layer.x - handleSize/2, y: layer.y + layer.height - handleSize/2, cursor: 'sw-resize', type: 'sw' },
            { x: layer.x - handleSize/2, y: layer.y + layer.height/2 - handleSize/2, cursor: 'w-resize', type: 'w' },
          ];

          handles.forEach(handle => {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
            ctx.strokeStyle = '#22c55e';
            ctx.lineWidth = 1;
            ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
          });
        }
      }
    });
  }, [layers, selectedLayerId, backgroundImage]);

  // Get mouse position relative to canvas
  const getCanvasPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale
    };
  }, [scale]);

  // Find layer at position
  const findLayerAtPosition = useCallback((x, y) => {
    // Check in reverse order (top layer first)
    const sortedLayers = [...layers].sort((a, b) => b.zIndex - a.zIndex);

    for (const layer of sortedLayers) {
      if (!layer.visible) continue;

      if (x >= layer.x && x <= layer.x + layer.width &&
          y >= layer.y && y <= layer.y + layer.height) {
        return layer;
      }
    }
    return null;
  }, [layers]);

  // Find resize handle at position
  const findResizeHandle = useCallback((x, y, layer) => {
    if (!layer || layer.locked) return null;

    const handleSize = 12; // Larger hit area
    const handles = [
      { x: layer.x, y: layer.y, type: 'nw' },
      { x: layer.x + layer.width/2, y: layer.y, type: 'n' },
      { x: layer.x + layer.width, y: layer.y, type: 'ne' },
      { x: layer.x + layer.width, y: layer.y + layer.height/2, type: 'e' },
      { x: layer.x + layer.width, y: layer.y + layer.height, type: 'se' },
      { x: layer.x + layer.width/2, y: layer.y + layer.height, type: 's' },
      { x: layer.x, y: layer.y + layer.height, type: 'sw' },
      { x: layer.x, y: layer.y + layer.height/2, type: 'w' },
    ];

    for (const handle of handles) {
      if (Math.abs(x - handle.x) < handleSize && Math.abs(y - handle.y) < handleSize) {
        return handle.type;
      }
    }
    return null;
  }, []);

  // Mouse handlers
  const handleMouseDown = useCallback((e) => {
    const pos = getCanvasPosition(e);
    const selectedLayer = layers.find(l => l.id === selectedLayerId);

    // Check for resize handle first
    if (selectedLayer) {
      const handle = findResizeHandle(pos.x, pos.y, selectedLayer);
      if (handle) {
        setIsResizing(true);
        setResizeHandle(handle);
        return;
      }
    }

    // Check for layer click
    const clickedLayer = findLayerAtPosition(pos.x, pos.y);
    if (clickedLayer) {
      setSelectedLayerId(clickedLayer.id);
      if (!clickedLayer.locked) {
        setIsDragging(true);
        setDragOffset({
          x: pos.x - clickedLayer.x,
          y: pos.y - clickedLayer.y
        });
      }
    } else {
      setSelectedLayerId(null);
    }
  }, [getCanvasPosition, findLayerAtPosition, findResizeHandle, layers, selectedLayerId]);

  // Double-click to edit text layers
  const handleDoubleClick = useCallback((e) => {
    const pos = getCanvasPosition(e);
    const clickedLayer = findLayerAtPosition(pos.x, pos.y);

    if (clickedLayer && clickedLayer.type === 'text' && !clickedLayer.locked) {
      setEditingTextLayerId(clickedLayer.id);
      setEditingTextValue(clickedLayer.isTextEdited ? clickedLayer.editedText : clickedLayer.content || '');
      // Focus the input after a short delay
      setTimeout(() => textInputRef.current?.focus(), 50);
    }
  }, [getCanvasPosition, findLayerAtPosition]);

  // Save edited text
  const saveEditedText = useCallback(() => {
    if (editingTextLayerId && editingTextValue.trim()) {
      setLayers(prev => prev.map(layer =>
        layer.id === editingTextLayerId
          ? { ...layer, isTextEdited: true, editedText: editingTextValue.trim() }
          : layer
      ));
    }
    setEditingTextLayerId(null);
    setEditingTextValue('');
  }, [editingTextLayerId, editingTextValue]);

  // Cancel text editing
  const cancelEditText = useCallback(() => {
    setEditingTextLayerId(null);
    setEditingTextValue('');
  }, []);

  // Update text style for selected layer
  const updateTextStyle = useCallback((styleKey, value) => {
    if (selectedLayerId) {
      setLayers(prev => prev.map(layer =>
        layer.id === selectedLayerId && layer.type === 'text'
          ? { ...layer, textStyle: { ...layer.textStyle, [styleKey]: value } }
          : layer
      ));
    }
  }, [selectedLayerId]);

  const handleMouseMove = useCallback((e) => {
    const pos = getCanvasPosition(e);

    if (isDragging && selectedLayerId) {
      setLayers(prev => prev.map(layer => {
        if (layer.id === selectedLayerId) {
          return {
            ...layer,
            x: Math.max(0, Math.min(canvasSize.width - layer.width, pos.x - dragOffset.x)),
            y: Math.max(0, Math.min(canvasSize.height - layer.height, pos.y - dragOffset.y))
          };
        }
        return layer;
      }));
    }

    if (isResizing && selectedLayerId && resizeHandle) {
      setLayers(prev => prev.map(layer => {
        if (layer.id !== selectedLayerId) return layer;

        let { x, y, width, height } = layer;
        const minSize = 20;

        switch (resizeHandle) {
          case 'se':
            width = Math.max(minSize, pos.x - x);
            height = Math.max(minSize, pos.y - y);
            break;
          case 'e':
            width = Math.max(minSize, pos.x - x);
            break;
          case 's':
            height = Math.max(minSize, pos.y - y);
            break;
          case 'nw':
            const newWidth = width + (x - pos.x);
            const newHeight = height + (y - pos.y);
            if (newWidth > minSize) { width = newWidth; x = pos.x; }
            if (newHeight > minSize) { height = newHeight; y = pos.y; }
            break;
          case 'ne':
            width = Math.max(minSize, pos.x - x);
            const neHeight = height + (y - pos.y);
            if (neHeight > minSize) { height = neHeight; y = pos.y; }
            break;
          case 'sw':
            const swWidth = width + (x - pos.x);
            if (swWidth > minSize) { width = swWidth; x = pos.x; }
            height = Math.max(minSize, pos.y - y);
            break;
          case 'n':
            const nHeight = height + (y - pos.y);
            if (nHeight > minSize) { height = nHeight; y = pos.y; }
            break;
          case 'w':
            const wWidth = width + (x - pos.x);
            if (wWidth > minSize) { width = wWidth; x = pos.x; }
            break;
        }

        return { ...layer, x, y, width, height };
      }));
    }
  }, [isDragging, isResizing, selectedLayerId, resizeHandle, dragOffset, getCanvasPosition, canvasSize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  }, []);

  // Layer operations
  const toggleLayerVisibility = (layerId) => {
    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, visible: !l.visible } : l
    ));
  };

  const toggleLayerLock = (layerId) => {
    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, locked: !l.locked } : l
    ));
  };

  const deleteLayer = (layerId) => {
    setLayers(prev => prev.filter(l => l.id !== layerId));
    if (selectedLayerId === layerId) {
      setSelectedLayerId(null);
    }
  };

  const bringToFront = (layerId) => {
    const maxZ = Math.max(...layers.map(l => l.zIndex));
    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, zIndex: maxZ + 1 } : l
    ));
  };

  const sendToBack = (layerId) => {
    const minZ = Math.min(...layers.map(l => l.zIndex));
    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, zIndex: minZ - 1 } : l
    ));
  };

  const resetLayers = () => {
    if (extractedLayers?.layers) {
      const resetLayers = extractedLayers.layers.map((layer, idx) => ({
        id: layer.id,
        type: layer.type,
        content: layer.content,
        image: layers.find(l => l.id === layer.id)?.image || null,
        imageSrc: layer.image,
        x: layer.bbox[0],
        y: layer.bbox[1],
        width: layer.bbox[2] - layer.bbox[0],
        height: layer.bbox[3] - layer.bbox[1],
        visible: true,
        locked: false,
        zIndex: idx + 1
      }));
      setLayers(resetLayers);
      setSelectedLayerId(null);
    }
  };

  // Render canvas state to a PNG data URL (without selection indicators)
  const renderCanvasToImage = useCallback(() => {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvasSize.width;
    exportCanvas.height = canvasSize.height;
    const ctx = exportCanvas.getContext('2d');

    // Draw background
    if (backgroundImage) {
      ctx.drawImage(backgroundImage, 0, 0, canvasSize.width, canvasSize.height);
    }

    // Draw layers (no selection indicators)
    const sortedLayers = [...layers].sort((a, b) => a.zIndex - b.zIndex);
    sortedLayers.forEach(layer => {
      if (!layer.visible) return;

      // If this is an edited text layer, draw text instead of image
      if (layer.type === 'text' && layer.isTextEdited && layer.editedText) {
        const style = layer.textStyle || {};
        ctx.font = `${style.fontWeight || 'bold'} ${style.fontSize || 48}px ${style.fontFamily || 'Arial, sans-serif'}`;
        ctx.fillStyle = style.color || '#ffffff';
        ctx.textBaseline = 'top';
        ctx.textAlign = style.textAlign || 'left';

        // Word wrap text within the layer bounds
        const words = layer.editedText.split(' ');
        let line = '';
        let y = layer.y + 10;
        const lineHeight = (style.fontSize || 48) * 1.2;
        const maxWidth = layer.width - 20;
        const startX = style.textAlign === 'center' ? layer.x + layer.width / 2 :
                       style.textAlign === 'right' ? layer.x + layer.width - 10 : layer.x + 10;

        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          if (metrics.width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), startX, y);
            line = words[i] + ' ';
            y += lineHeight;
          } else {
            line = testLine;
          }
        }
        ctx.fillText(line.trim(), startX, y);
      } else if (layer.image) {
        ctx.drawImage(layer.image, layer.x, layer.y, layer.width, layer.height);
      }
    });

    return exportCanvas.toDataURL('image/png');
  }, [canvasSize, backgroundImage, layers]);

  // Export canvas as image (download or pass to parent)
  const handleExport = () => {
    const dataUrl = renderCanvasToImage();

    if (onExport) {
      // Pass both the image and the updated layers so edits can be preserved
      onExport(dataUrl, layers);
    } else {
      const link = document.createElement('a');
      link.download = 'edited-slide.png';
      link.href = dataUrl;
      link.click();
    }
  };

  // Save edits: render canvas to image, pass both layers and image to parent
  const handleSave = () => {
    if (!onSave) return;
    const dataUrl = renderCanvasToImage();
    onSave(layers, dataUrl);
  };

  const selectedLayer = layers.find(l => l.id === selectedLayerId);

  return (
    <div className="fixed inset-0 bg-black/80 flex z-50">
      {/* Left Panel - Layer List */}
      <div className="w-64 bg-white dark:bg-slate-900 border-r flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-sm">Layers</h3>
          <Badge variant="outline">{layers.length}</Badge>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {[...layers].sort((a, b) => b.zIndex - a.zIndex).map(layer => (
            <div
              key={layer.id}
              onClick={() => setSelectedLayerId(layer.id)}
              className={`p-2 rounded-md cursor-pointer transition-colors ${
                selectedLayerId === layer.id
                  ? 'bg-green-100 dark:bg-green-900/30 border border-green-500'
                  : 'hover:bg-slate-100 dark:hover:bg-slate-800 border border-transparent'
              }`}
            >
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleLayerVisibility(layer.id); }}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                >
                  {layer.visible ? (
                    <Eye className="w-3 h-3" />
                  ) : (
                    <EyeOff className="w-3 h-3 text-muted-foreground" />
                  )}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleLayerLock(layer.id); }}
                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                >
                  {layer.locked ? (
                    <Lock className="w-3 h-3 text-orange-500" />
                  ) : (
                    <Unlock className="w-3 h-3 text-muted-foreground" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate flex items-center gap-1">
                    {layer.type}
                    {layer.extractionMethod === 'bbox_crop_fallback' && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400" title="Shape boundary is approximate">approx</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {layer.content || 'No content'}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteLayer(layer.id); }}
                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Layer Actions */}
        {selectedLayer && (
          <div className="p-3 border-t space-y-2">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Selected: {selectedLayer.type}
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => bringToFront(selectedLayerId)}
              >
                Bring Front
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => sendToBack(selectedLayerId)}
              >
                Send Back
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="h-12 bg-white dark:bg-slate-900 border-b flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setScale(s => Math.max(0.1, s - 0.1))}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium w-16 text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setScale(s => Math.min(2, s + 0.1))}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <div className="w-px h-6 bg-border mx-2" />
            <Button
              size="sm"
              variant="outline"
              onClick={resetLayers}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            {onSave && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSave}
              >
                <Save className="w-4 h-4 mr-1" />
                Save Edits
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleExport}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
            >
              <Download className="w-4 h-4 mr-1" />
              Export Image
            </Button>
          </div>
        </div>

        {/* Canvas Container */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-slate-800 flex items-center justify-center p-8"
        >
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              width: canvasSize.width * scale,
              height: canvasSize.height * scale,
              cursor: isDragging ? 'grabbing' : (selectedLayerId ? 'grab' : 'default')
            }}
            className="shadow-2xl"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onDoubleClick={handleDoubleClick}
          />
        </div>

        {/* Status Bar */}
        <div className="h-8 bg-white dark:bg-slate-900 border-t flex items-center justify-between px-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>Canvas: {canvasSize.width} × {canvasSize.height}</span>
            {inpaintingSuccess && (
              <Badge variant="outline" className="text-green-600 border-green-600">
                ✓ Clean Background
              </Badge>
            )}
          </div>
          {selectedLayer && (
            <span>
              Position: ({Math.round(selectedLayer.x)}, {Math.round(selectedLayer.y)}) |
              Size: {Math.round(selectedLayer.width)} × {Math.round(selectedLayer.height)}
              {selectedLayer.type === 'text' && ' | Double-click to edit text'}
            </span>
          )}
        </div>
      </div>

      {/* Text Editing Modal */}
      {editingTextLayerId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Type className="w-5 h-5 text-green-500" />
              Edit Text
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Text Content</label>
                <textarea
                  ref={textInputRef}
                  value={editingTextValue}
                  onChange={(e) => setEditingTextValue(e.target.value)}
                  className="w-full p-3 border rounded-md min-h-[100px] resize-none"
                  placeholder="Enter text..."
                />
              </div>

              {/* Text Style Controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Font Size</label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={layers.find(l => l.id === editingTextLayerId)?.textStyle?.fontSize || 48}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setLayers(prev => prev.map(l =>
                        l.id === editingTextLayerId
                          ? { ...l, textStyle: { ...l.textStyle, fontSize: value } }
                          : l
                      ));
                    }}
                  >
                    <option value={24}>24px</option>
                    <option value={32}>32px</option>
                    <option value={48}>48px</option>
                    <option value={64}>64px</option>
                    <option value={72}>72px</option>
                    <option value={96}>96px</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Font Weight</label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={layers.find(l => l.id === editingTextLayerId)?.textStyle?.fontWeight || 'bold'}
                    onChange={(e) => {
                      setLayers(prev => prev.map(l =>
                        l.id === editingTextLayerId
                          ? { ...l, textStyle: { ...l.textStyle, fontWeight: e.target.value } }
                          : l
                      ));
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Text Color</label>
                  <input
                    type="color"
                    className="w-full h-10 p-1 border rounded-md cursor-pointer"
                    value={layers.find(l => l.id === editingTextLayerId)?.textStyle?.color || '#ffffff'}
                    onChange={(e) => {
                      setLayers(prev => prev.map(l =>
                        l.id === editingTextLayerId
                          ? { ...l, textStyle: { ...l.textStyle, color: e.target.value } }
                          : l
                      ));
                    }}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Text Align</label>
                  <select
                    className="w-full p-2 border rounded-md"
                    value={layers.find(l => l.id === editingTextLayerId)?.textStyle?.textAlign || 'left'}
                    onChange={(e) => {
                      setLayers(prev => prev.map(l =>
                        l.id === editingTextLayerId
                          ? { ...l, textStyle: { ...l.textStyle, textAlign: e.target.value } }
                          : l
                      ));
                    }}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={cancelEditText}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                onClick={saveEditedText}
                className="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
              >
                <Check className="w-4 h-4 mr-1" />
                Save Text
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
